/* eslint-disable array-callback-return */
import { AxiosError } from "axios";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useLocation, useParams } from "react-router-dom";
import { getContractApplicationInfo, getContractDetailBasicInfo, getContractManage, getContractOperationInfo } from "src/api/contract/contract-api";
import { ContractStep } from "src/api/contract/contract-types";
import { errorMessageHandler } from "src/api/error/contract-response-error";
import { useApiOperation } from "src/api/hooks";
import { contract_bill_user_errors } from "src/api/public-types";
import { addUsers } from "src/api/user/user-api";
import { BaseButton, BaseModal, BaseSelect, ContentsTitle } from "src/components";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import EditCancelButton from "src/components/EditCancelButton";
import MetaTag from "src/components/layout/MetaTag";
import useNavigate from "src/hooks/usePartnerNavigate";
import PagePath from "src/pagePath.json";
import useContractApi from "src/pages/contract/hooks/useContractApi";
import { findRepresentativeContract } from "src/pages/contract/hooks/useContractRenewal";
import { useModal } from "src/recoil/modalState/hook";
import { useToast } from "src/recoil/toast/hook";
import { parsedPhoneNumber } from "src/utils";
import { getContractDetailPath } from "src/utils/route-util";
import UserBaseModal from "../components/UserBaseModal";
import { INVITE_EXPIRE_DAYS_OPTIONS, INVITEABLE_CONTRACT_STEP } from "../constants";
import UserInfo from "./UserInfo";

// UserInsertModel을 확장하여 상태, 에러 메시지 등을 추가한 모델
export interface UserMakeModel {
  status: string;
  errorMessage?: string;
  errorCode?: string;
  inviteMobileNumber: string;
  inviteEmail?: string;
  invitationType?: "phone" | "email";
}

export interface UserFormData {
  users: UserMakeModel[];
}

export const defaultUserValue = { inviteMobileNumber: "", status: "READY", inviteEmail: "", invitationType: "phone" } as UserMakeModel;

const UserForm = () => {
  // 페이지 이동을 위한 커스텀 네비게이션 훅
  const navigate = useNavigate();

  const { setBaseModal } = useModal();

  // 현재 라우트의 위치 정보
  const location = useLocation();
  // URL 파라미터 정보
  const pathParams = useParams();

  // 쿼리스트링 파싱 (URL의 쿼리 파라미터를 객체로 변환)
  const params = useMemo(
    () =>
      qs.parse(location.search, {
        ignoreQueryPrefix: true,
        allowDots: true,
      }),
    [location],
  );

  const useFormReturn = useForm<UserFormData>({
    defaultValues: {
      //이용자 정보
      users: [],
    },
  });
  const { control, trigger, getValues, watch } = useFormReturn;

  const {
    fields: formUserList,

    append,
    remove,
    update,
  } = useFieldArray({
    control,

    name: "users",
  });

  // 운영 정보 상태
  const [operation, setOperation] = useState<any>({});

  // 최대 인원 초과 여부
  const [limitOver, setLimitOver] = useState(false);
  // 결과 모달 표시 여부
  const [resultModalVisible, setResultModalVisible] = useState(false);
  // 실패 사유 모달 표시 여부
  const [failReasonModalVisible, setFailReasonModalVisible] = useState(false);
  // 초대 확인 모달 표시 여부
  const [isConfirm, setIsConfirm] = useState(false);
  // 일반 에러 모달 표시 여부
  const [isError, setIsError] = useState(false);
  // 기타 에러 모달 표시 여부
  const [isError2, setIsError2] = useState(false);
  // 에러 메시지 상태
  const [message, setMessage] = useState("");
  // 계약 정보 상태
  const [contract, setContract] = useState<any>();
  // 전체 계약 정보 상태
  const [contractAll, setContractAll] = useState<any>();

  // 초대 만료 기한 상태
  const [inviteExpireDays, setInviteExpireDays] = useState<number>(3); // 기본값 3일

  // 토스트 알림 훅
  const { openToast } = useToast();

  // 이용자 등록 API 호출 함수
  const { executeAsync: add } = useApiOperation(addUsers, { noErrorModal: true });
  // 계약 신청 정보 조회 API 호출 함수
  const { executeAsync: getApplication } = useApiOperation(getContractApplicationInfo, { noErrorModal: true });
  // 운영 정보 조회 API 호출 함수
  const { executeAsync: getOperation } = useApiOperation(getContractOperationInfo, { noErrorModal: true });
  // 계약 관리 정보 조회 API 호출 함수
  const { executeAsync: checkContract } = useApiOperation(getContractManage, { noErrorModal: true });
  // 계약 기본 정보 조회 API 호출 함수 (로딩 없음)
  const { executeAsync: getContractDetailBasicInfoAsync } = useApiOperation(getContractDetailBasicInfo, { noLoading: true });
  // 계약 상세 정보 조회 커스텀 훅
  const { getContractDetail } = useContractApi();

  // 계약 관리 번호로 계약 기본 정보 조회 함수
  const checkContractBasic = useCallback(
    async (contractApplyNumber: string) => {
      const res = await checkContract({ contractApplyNumber });
      setContract(res.data.data);
      return res.data.data;
    },
    [checkContract],
  );

  /**
   * 이용자 초대(추가) 함수
   * - 전달받은 이용자 리스트(contractMemberList)를 하나씩 addUsers API로 등록 시도
   * - contractId(계약 ID)와 partnerId를 함께 전송
   * - 각 이용자별로 성공/실패 여부와 에러 메시지를 상태에 반영
   * - 에러코드가 eCT601(중복 등 특수 케이스)이면 즉시 반복문 종료
   * - 모든 처리 후 결과 모달을 표시
   * @param contractMemberList 초대할 이용자 목록
   */
  const handleAddUsers = async () => {
    const contractMemberList = getValues().users;

    setIsConfirm(false);
    console.log(contractMemberList);
    // 이용자 리스트를 하나씩 반복 처리
    for (let i = 0; i < contractMemberList.length; i++) {
      // 재시도 시 이전 실패 상태 초기화
      contractMemberList[i] = Object.assign(contractMemberList[i], {
        status: "READY",
        errorMessage: "",
        errorCode: "",
      });

      // invitationType에 따라 해당 필드만 전송
      const isPhone = contractMemberList[i]?.invitationType !== "email";

      //이용자 초대 요청 body
      const reqBody = {
        contractManageId: contract?.contractManage?.contractManageId,
        contractMemberList: [
          {
            inviteMobileNumber: isPhone ? parsedPhoneNumber(contractMemberList[i]?.inviteMobileNumber) : "",
            inviteExpireDays: Number(inviteExpireDays),
            inviteEmail: isPhone ? "" : contractMemberList[i]?.inviteEmail,
          },
        ],
      };

      // addUsers API 호출
      let response: any;
      try {
        response = await add(reqBody);
      } catch (error: any) {
        response = error?.response;
      }

      const resStatus = response?.status ?? 0;
      const resErrorCode = response?.data?.meta?.errorCode ?? "";

      // 성공 시 상태 SUCCESS로 변경
      if (resStatus >= 200 && resStatus <= 299) {
        contractMemberList[i] = Object.assign(contractMemberList[i], {
          status: "SUCCESS",
          errorMessage: "",
        });
      } else {
        // 실패 시 에러 메시지 처리
        let mes = "";
        if (resErrorCode !== "" && resErrorCode !== "eCT601") {
          mes = errorMessageHandler(resStatus, resErrorCode, contract_bill_user_errors);
        } else if (!response) {
          mes = errorMessageHandler(resStatus, "", contract_bill_user_errors);
        }
        // 특수 에러코드(eCT601)면 즉시 반복문 종료
        if (resErrorCode === "eCT601") {
          contractMemberList[i] = Object.assign(contractMemberList[i], {
            status: "FAIL",
            errorMessage: errorMessageHandler(resStatus, resErrorCode, contract_bill_user_errors),
            errorCode: resErrorCode,
          });
          update(i, contractMemberList[i]);
          break;
        }
        // 일반 실패 처리
        contractMemberList[i] = Object.assign(contractMemberList[i], {
          status: "FAIL",
          errorMessage: mes,
          errorCode: resErrorCode,
        });
      }
      // 상태 업데이트(성공/실패 반영)
      update(i, contractMemberList[i]);
    }
    // 결과 모달 표시
    setResultModalVisible(true);
  };

  // 목록 페이지로 이동 함수
  const moveList = useCallback(() => {
    setResultModalVisible(false);
    let path = PagePath.user.list + "?" + qs.stringify(params, { allowDots: true, encode: true });
    path = path + "&contractId=" + pathParams.contractManageId;
    navigate(path);
  }, [navigate, params]);

  const onSubmit = async () => {
    if (formUserList.length === 0) {
      //1명이상 등록가능
      return setBaseModal({
        isOpen: true,
        title: "1명 이상의 이용자 등록이 필요합니다.",
        btnLeftTitle: "취소",
        btnRightTitle: "확인",
        onClick: () => {
          setBaseModal({ isOpen: false });
        },
      });
    }
    //유효검사 트리거
    const isValid = await trigger();
    //유효검사 통과시 초대 컨펌 모달 오픈
    if (isValid) {
      setIsConfirm(true);
    }
  };

  // 계약, 운영 정보 등 여러 API를 조합하여 정보 조회
  const getContractInfos = useCallback(async () => {
    const showError = (error: unknown) => {
      const errorResponse = error instanceof AxiosError ? error.response : undefined;
      setMessage(errorMessageHandler(errorResponse?.status ?? 0, errorResponse?.data?.meta?.errorCode, contract_bill_user_errors));
      setIsError2(true);
      return undefined;
    };

    const ct = await checkContractBasic(String(params.contractApplyNumber)).catch(showError);
    const application: any = await getApplication({ contractId: Number(pathParams.contractManageId) }).catch(showError);
    if (application) {
      // 신청계약번호 링크 등 화면 표기는 대표 계약(이용중 우선, 없으면 최신 계약) 기준으로 보여준다.
      // RCA18의 단건 contractId는 항상 원계약이라 재계약이 있으면 종료된 계약이 노출된다
      const targetContractId = findRepresentativeContract(ct?.contracts)?.contractId ?? ct?.contractId;
      const ctAll = targetContractId ? await getContractDetail(String(targetContractId)) : undefined;

      if (ctAll?.product) {
        setContractAll(ctAll);
      }
    }
    const resOper: any = await getOperation({ id: Number(pathParams.contractManageId) }).catch(showError);
    if (resOper) {
      setOperation(resOper.data.data.content.productManage);
    }
  }, [checkContractBasic, getApplication, getOperation, params.contractApplyNumber, pathParams.contractManageId]);

  // 계약 상세 정보 및 상태 체크 함수
  const checkContractDetail = useCallback(() => {
    (async () => {
      try {
        const result = await getContractDetailBasicInfoAsync({ id: Number(pathParams.contractManageId) });
        const _contractApplyNumber = result.data.data.contractApplyNumber;
        const contractStep = result.data.data.contractStep;
        if (_contractApplyNumber !== params.contractApplyNumber) {
          openToast({
            type: "FAIL",
            content: "잘못된 접근입니다.",
          });
          navigate(PagePath.user.list);
        }
        if (!INVITEABLE_CONTRACT_STEP.includes(contractStep as ContractStep)) {
          openToast({
            type: "FAIL",
            content: "초대할 수 없는 계약 상태입니다.",
          });
          navigate(PagePath.user.list + `?contractApplyNumber=${params.contractApplyNumber}&contractId=${pathParams.contractManageId}`);
        }
      } catch (error) {
        openToast({
          type: "FAIL",
          content: "계약 정보를 불러오는데 실패하였습니다.",
        });
        navigate(PagePath.user.list);
      }
    })();
  }, [getContractDetailBasicInfoAsync, navigate, openToast, params.contractApplyNumber, pathParams.contractManageId]);

  // 최초 렌더링 시 계약 정보 및 상세 정보 조회
  useEffect(() => {
    if (pathParams?.contractManageId) {
      getContractInfos();
      checkContractDetail();
    }
  }, [pathParams.contractManageId]);

  return (
    <>
      <MetaTag isCreatePage />
      <div>
        <ContentsTitle
          title="이용자 초대"
          tabs={[
            {
              label: "기본정보",
              value: "basic",
            },
          ]}
          activeTab={{
            label: "기본정보",
            value: "basic",
          }}
        />
        <div className="contents-container__scroll">
          <div className="contents-container__wrap page-user-form">
            <article className="contents-container__wrap-article">
              <div className="contents-container__sub-title">
                <h2>초대 정보</h2>
              </div>
              <div className="flex-row flex-center">
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p>계약관리 ID</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <div className="d-flex flex-row flex-center-start">
                      {/* 계약관리 ID 단독 표기이므로 대표 계약(이용중 우선, 없으면 최신) 상세로 링크한다 */}
                      <BaseNewTabLink
                        path={getContractDetailPath({
                          contractId: contractAll?.contract?.contractId?.toString() ?? "",
                        })}
                        value={contractAll?.contractManage?.contractManageId?.toString() || "-"}
                      />
                    </div>
                  </div>
                </section>
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p>계약명</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <p>{contractAll?.contractManage?.contractName || "-"}</p>
                  </div>
                </section>
              </div>

              <div className="flex-row flex-center">
                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p>상품명</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <p>{contractAll?.product?.productName || "-"}</p>
                  </div>
                </section>

                <section className="contents-container__grid">
                  <div className="contents-container__grid-index">
                    <p>이용중 인원</p>
                  </div>
                  <div className="contents-container__grid-contents">
                    <p>
                      {operation?.memberNums !== undefined ? operation?.memberNums : "-"}명 이용중(총{" "}
                      {operation?.memberMaxNums !== undefined ? operation?.memberMaxNums : "-"}명 가능)
                    </p>
                  </div>
                </section>
              </div>
            </article>
            <article className="contents-container__wrap-article">
              <div className="contents-container__sub-title">
                <h2>이용자 설정</h2>
              </div>
              <section className="contents-container__grid">
                <div className="contents-container__grid-index">
                  <p className="required">초대 만료 기한</p>
                </div>
                <div className="contents-container__grid-contents">
                  {/* 만료기한 설정 정책
                  https://pnpt.atlassian.net/wiki/spaces/dsplan/pages/1008567228/Ctrl.room#%EC%9D%B4%EC%9A%A9%EC%9E%90-%3E-%EC%9D%B4%EC%9A%A9%EC%9E%90-%EC%B4%88%EB%8C%80-%ED%99%94%EB%A9%B4-%3E-%EC%84%A4%EC%A0%95 */}
                  <BaseSelect
                    className="minmax200"
                    stateOptions={INVITE_EXPIRE_DAYS_OPTIONS}
                    setStateValue={setInviteExpireDays}
                    value={inviteExpireDays}
                  />
                </div>
              </section>
            </article>

            <UserInfo control={control} formUserList={formUserList} append={append} remove={remove} useFormReturn={useFormReturn} />
          </div>
          <BaseModal
            title="초대하시겠습니까?"
            isOpen={isConfirm}
            btnLeftTitle={"취소"}
            btnRightTitle={"확인"}
            onClose={() => setIsConfirm(false)}
            onClick={() => handleAddUsers()}
          >
            <div>
              <p>초대 받은 이용자들에게 알림톡이 발송되며, 앱 설치 및 회원가입을 하면 공간에 이용권한이 부여됩니다.</p>
            </div>
          </BaseModal>

          {resultModalVisible &&
            (formUserList.every((user) => user.status === "SUCCESS") ? (
              <BaseModal title="초대를 완료했어요" className="small-size" isOpen={true} btnRightTitle={"확인"} onClick={() => moveList()}>
                <div>성공 : {formUserList.filter((user: UserMakeModel) => user.status === "SUCCESS").length}건 / 실패 : 0건</div>
              </BaseModal>
            ) : (
              <BaseModal
                title="초대를 완료했어요"
                className="small-size"
                isOpen={true}
                btnLeftTitle={"확인"}
                btnRightTitle={"실패 사유 보기"}
                onClose={() => moveList()}
                onClick={() => {
                  setResultModalVisible(false);
                  setFailReasonModalVisible(true);
                }}
              >
                <div>
                  성공 : {formUserList.filter((user: UserMakeModel) => user.status === "SUCCESS").length}건 / 실패 :{" "}
                  {formUserList.filter((user: UserMakeModel) => user.errorCode === "eCT601" && user.status === "FAIL").length > 0
                    ? formUserList.length
                    : formUserList.filter((user: UserMakeModel) => user.status === "FAIL").length}{" "}
                  건
                </div>
              </BaseModal>
            ))}

          <BaseModal title="일시적인 오류가 발생하였습니다." isOpen={isError} btnRightTitle={"확인"} onClick={() => setIsError(false)}>
            <div>
              <p>나중에 다시 시도해 주세요</p>
            </div>
          </BaseModal>
          <BaseModal title="초대에 실패하였습니다." isOpen={limitOver} btnRightTitle={"확인"} onClick={() => setLimitOver(false)}>
            <div>
              <p>최대 인원을 초과하여 이용자 초대에 실패하였습니다. 기존의 이용자를 삭제 후 초대해 주세요.</p>
            </div>
          </BaseModal>

          <BaseModal title={message} isOpen={isError2} btnRightTitle={"확인"} onClick={() => setIsError2(false)} />

          {failReasonModalVisible && (
            <UserBaseModal title="아래 사유로 초대에 실패했어요" className="base-modal dialog-modal">
              <div className="user-base-modal-title">아래 사유로 초대에 실패했어요</div>
              <div className="user-base-modal-body">
                <div className="user-base-modal-body-subtitle">
                  <span className="user-base-modal-body-subtitle-fail">실패</span>
                  <span className="user-base-modal-body-subtitle-count">({`${formUserList.filter((user) => user.status === "FAIL").length}건`})</span>
                </div>
                <ul className="user-base-modal-body-list">
                  {formUserList
                    ?.filter((user: UserMakeModel) => user.status === "FAIL")
                    .map((user: UserMakeModel, index: number) => {
                      return (
                        <li key={index}>
                          <span className="user-base-modal-body-list-name">
                            {user.invitationType === "email" ? user.inviteEmail : user.inviteMobileNumber}
                          </span>
                          <span className="user-base-modal-body-list-reason">{user.errorMessage}</span>
                        </li>
                      );
                    })}
                </ul>
              </div>
              <div className="user-base-modal-footer">
                <BaseButton
                  title="확인"
                  onClick={() => {
                    // 이전 실패 상태 초기화
                    formUserList.forEach((user, index) => {
                      if (user.status === "FAIL") {
                        update(index, { ...user, status: "READY", errorMessage: "", errorCode: "" });
                      }
                    });
                    setFailReasonModalVisible(false);
                  }}
                />
              </div>
            </UserBaseModal>
          )}
        </div>
        <div className="contents-container__btn-wrap">
          <div className="left-area d-flex"></div>
          <div className="right-area">
            <EditCancelButton to={PagePath.user.list} />
            <BaseButton title="저장" onClick={onSubmit} className="size-large" />
          </div>
        </div>
      </div>
    </>
  );
};
export default UserForm;
